<template>
  <div v-if="isReady" class="ApplicationView isEditMode">
    <ApplicationBanner :content="'<h1>' + setHeading + '</h1>'" />

    <b-container class="content">
      <b-row>
        <b-col lg="4">
          <div class="sideContent">
            <ApplicationMenu />
          </div>
        </b-col>

        <b-col lg="8" class="mainContent">
          <ContainerApplicationReview v-if="sectionExpandedId === 'review'" />
          <ApplicationSection />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import base64url from 'base64url'
import { enableShelterSection } from '@/applicationDefinition/helper/serviceHelper'

import ContainerApplicationReview from '@/containers/application/ContainerApplicationReview.vue'

import ApplicationBanner from '@/components/application/ApplicationBanner.vue'
import ApplicationMenu from '@/components/application/ApplicationMenu.vue'
import ApplicationSection from '@/components/application/ApplicationSection.vue'

export default {
  name: 'ApplicationView',
  props: ['isUnitTesting'],
  components: {
    ApplicationBanner,
    ApplicationMenu,
    ApplicationSection,
    ContainerApplicationReview
  },
  methods: {
    getAllData() {
      this.$store.dispatch('getSections')
      this.$store.dispatch('showSpinner')
      this.getReferenceData()
      this.getApplication()
      this.getSupportingDocConfig()
      this.getIdentityProofDocConfig()
      this.getCounties()
    },

     getCounties() {
      this.$store
        .dispatch('getCounties')
        .then(() => {
          this.proceedIfReady()
        })
        .catch(e => {
          this.showError(e)
        })
    },

    getReferenceData() {
      this.$store
        .dispatch('getReferenceData')
        .then(() => {
          this.proceedIfReady()
        })
        .catch(e => {
          this.showError(e)
        })
    },

    getSupportingDocConfig() {
      const oesProperties = this.$store.state.oesProperties
      if (oesProperties && oesProperties.supportingDocuments) {
        this.proceedIfReady()
      } else {
        this.$store
          .dispatch('getOESProperties')
          .then(() => {
            this.proceedIfReady()
          })
          .catch(e => {
            this.showError(e)
          })
      }
    },

     getIdentityProofDocConfig() {
      const oesProperties = this.$store.state.oesProperties
      if (oesProperties && oesProperties.identityProofDocuments) {
        this.proceedIfReady()
      } else {
        this.$store
          .dispatch('getOESProperties')
          .then(() => {
            this.proceedIfReady()
          })
          .catch(e => {
            this.showError(e)
          })
      }
    },

    getApplication() {
      /*
        A saved application should always be available at this point. Even for a new
        application, the landing pages will have already saved initial data HOWEVER...

        Dynamo DB is currently using "Eventually consistent" reads, meaning that
        when PUTting an application, the success response may arrive before tables have
        finished writing. This means that when the landing pages PUT the initial application
        data, this getApplication() might be called before the data has been written, and
        therefore will return no application data.

        Currently the best solution is to
        wait and try again if no application data is returned (hence the setTimeout).
      */
      this.$store
        .dispatch('getApplication')
        .then(() => {
          // reference application in store.state directly instead of this.application as Vue may not have updated
          // this.application in time, causing a race condition.
          if (!this.$store.state.application) {
            // Wait and retry if no application data (see essay above)
            setTimeout(() => {
              this.getApplication()
            }, 250)
          } 
        })
        .catch(e => {
          this.showError(e)
        })
    },   

    proceedIfReady() {
     
      if (this.isReady) {
        this.$store.dispatch('hideSpinner')

this.$store.dispatch('buildCounters')
        if (this.application.meta && this.application.meta.hasAttemptToSubmit) {
          this.$store.dispatch('buildAlerts')
        }
      }
    },

    getLabel(field, application) {
      if (field && typeof field.label === 'function') {
        return field.label(application)
      } else if (field && field.label) {
        return field.label
      }
    },

    showError(error) {
      // eslint-disable-next-line no-console
      console.log(error)
      this.$store.dispatch('set', ['error', new Error(error)])
      this.$router.history.push('/error')
      this.$store.dispatch('hideSpinner')
    }
  },
  computed: {
    application() {
      return this.$store.state.application
    },

    isReady() {
      let state = this.$store.state
      return (
        this.application &&
        state.refData &&        
        state.oesProperties &&
        state.oesProperties.supportingDocuments     
      )
    },

    sectionExpandedId() {
      try {
        return this.application.meta.sectionExpandedId || ''
      } catch (e) {
        return ''
      }
    },

    setHeading() {
      enableShelterSection(' ', this.$store.state.application)
      if (this.sectionExpandedId !== 'review') {
        let field = this.$store.state.sections.filter(
          val => val.id === this.sectionExpandedId
        )[0].fields[0]
        return this.getLabel(field, this.application)
      } else {
        return 'Review'
      }
    }
  },

  mounted() {
    // Make sure there is an auth session
    if (!this.$store.state.auth || !this.$store.state.auth.idToken) {
      this.$router.history.push('/login/resume')
      return false
    }

    /*
      Once we are in the form, isNewApplication should be cleared. This is necessary
      to prevent issues if we go back to the OTP page (using the back button) and
      enter a new email. We need this scenario to operate with the "resume" flow,
      whereby this new unknown email would show the "No application found" page (allowing
      the user to start a new application or enter a different email).

      If we persisted with the "new application" flow when going back to the OTP page,
      a new application would be created with the wrong data (because it would proceed
      with application data already in state).
    */
    this.$store.dispatch('set', ['isNewApplication', false])

    // Gets all our form data...
    if (!this.isUnitTesting) {
      this.getAllData()
    }

  }
}
</script>

<style lang="scss">
.ApplicationView {
  margin: 0;

  &.isEditMode {
    .ApplicationBanner {
      margin-bottom: $header-height;
      height: 102px;
      max-height: 102px;
      position: relative;
      padding: 15px 15px 10px 15px;
      .headline {
        .bannerTitle {
          position: absolute;
          transform: none;
          bottom: 0;
          top: auto;
        }
        .bgLeftElement {
          height: 360px;
          width: 360px;
          left: -300px;
          bottom: -190px;
        }
        .bgRightElement {
          height: 196px;
          width: 523px;
          right: -190px;
          bottom: -90px;
        }
      }
    }
  }
}
@include desktop {
  #app {
    .isEditMode {
      .ApplicationBanner {
        .headline {
          .bannerTitle {
            position: absolute;
            transform: none;
            bottom: 0;
            top: auto;
          }
          h1 {
            font-size: 40px;
          }
          .bgLeftElement {
            left: -344px;
            bottom: -292px;
          }
          .bgRightElement {
            right: -210px;
            bottom: -120px;
          }
        }
      }
      .content {
        width: 100%;
        height: 100%;
      }

      .sideContent {
        position: relative;
        top: 2rem;
        display: block;
        padding: 2rem 0;
        min-height: 100%;
      }

      .mainContent {
        width: calc(100% - #{$desktop-sidebar-width});
        padding-top: 30px;
      }

      .isEditMode .sideContent {
        // Extra padding for when save bar is visible, so bottom content can be reached
        padding-bottom: $save-bar-height;
      }

      .isEditMode .mainContent {
        padding-bottom: $save-bar-height;
      }
    }
  }
}

@include tablet-wide {
  #app {
    .isEditMode {
      .ApplicationBanner {
        .headline {
          .bannerTitle {
            position: absolute;
            transform: none;
            bottom: 0;
            top: auto;
          }
          .bgLeftElement {
            left: -240px;
            bottom: -180px;
          }
          .bgRightElement {
            right: -330px;
            bottom: -50px;
          }
        }
      }
    }
    .container {
      .sideContent {
        padding: 4rem 0 0;
      }
      .mainContent {
        padding: 0;
      }
    }
  }
}

@include tablet {
  #app {
    .isEditMode {
      .ApplicationBanner {
        height: 82px;
        max-height: 82px;
        .headline {
          .bannerTitle {
            position: absolute;
            transform: none;
            bottom: 0;
            top: auto;
            h1 {
              font-size: 28px;
            }
          }
          .bgRightElement {
            right: -350px;
            bottom: -70px;
          }
          .bgLeftElement {
            left: -180px;
            bottom: -160px;
          }
        }
      }
      .container {
        .sideContent {
          padding: 3rem 0 0 2rem;
        }
        .mainContent {
          padding: 0 2rem;
        }
      }
    }
  }
}

@include mobile {
  #app {
    .isEditMode {
      .ApplicationBanner {
        height: 68px;
        max-height: 68px;
        margin: 0 -15px;
        .headline {
          .bannerTitle {
            left: 10px;
            transform: none;
            h1 {
              font-size: 21px;
              padding: 0 10px;
              overflow-wrap: break-word;
              word-wrap: break-word;
            }
          }
          .bgLeftElement {
            height: 130px;
            left: -115px;
            bottom: -90px;
          }
          .bgRightElement {
            height: 90px;
            right: -400px;
            bottom: -48px;
          }
        }
      }
      margin: 0 15px;
      .container {
        .sideContent {
          padding: 0;
        }

        .mainContent {
          form {
            &.ApplicationSection {
              & ::v-deep .ApplicationSaveCancel {
                padding: 0;

                button {
                  &.primary {
                    margin: 0.5rem 0 0;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
